import { errorFromResponse } from './errors'
import type { Me, RuntimeConfig } from './types'

/**
 * The single place the browser talks to the BFF.
 *
 * Three properties every call inherits, and each exists because getting it
 * wrong is invisible until production:
 *
 *  - `credentials: 'same-origin'` — the session lives in an HttpOnly cookie.
 *    The SPA holds no token and cannot attach one; if this is omitted the
 *    call is simply anonymous and the symptom is a redirect loop, not an
 *    error.
 *  - `X-CSRF-Token` on every unsafe method, plus a same-origin request so the
 *    browser sends `Origin`. The BFF's guard (bff/src/http/csrf.ts) requires
 *    BOTH; sending neither is a 403 that looks like an auth bug.
 *  - `signal` is passed through, always. The brief is explicit that BigQuery
 *    will answer in 1-3s, so a filter change that does not cancel the
 *    in-flight request is a guaranteed out-of-order render.
 */

const UNSAFE = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

export interface RequestOptions {
  readonly method?: string
  readonly body?: unknown
  readonly signal?: AbortSignal
  /** Required for unsafe methods. Comes from GET /bff/me; the SPA never
   *  invents or caches it independently of the session it belongs to. */
  readonly csrfToken?: string | undefined
}

export async function bffRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET'
  const headers: Record<string, string> = { accept: 'application/json' }

  if (UNSAFE.has(method)) {
    if (!options.csrfToken) {
      // Fail here rather than letting the BFF answer 403: a missing token is
      // a programming error in the caller, and a 403 would be indistinguishable
      // from a genuinely expired session.
      throw new Error(`${method} ${path} requires a csrfToken`)
    }
    headers['x-csrf-token'] = options.csrfToken
  }
  // FormData is passed through untouched and WITHOUT a content-type header:
  // the browser sets `multipart/form-data` with its own boundary, and setting
  // it here would produce a boundary-less header the server cannot parse.
  const isForm = options.body instanceof FormData
  if (options.body !== undefined && !isForm) headers['content-type'] = 'application/json'

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.body !== undefined ? { body: isForm ? options.body : JSON.stringify(options.body) } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  })

  if (!response.ok) throw await errorFromResponse(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** GET /bff/me — 200 for anonymous too, never 401, so this is safe to call
 *  before knowing whether anyone is signed in. */
export const fetchMe = (signal?: AbortSignal): Promise<Me> =>
  bffRequest<Me>('/bff/me', signal ? { signal } : {})

export const fetchRuntimeConfig = (signal?: AbortSignal): Promise<RuntimeConfig> =>
  bffRequest<RuntimeConfig>('/bff/config', signal ? { signal } : {})

/**
 * POST /bff/logout returns `{ logoutUrl }` for the caller to navigate to;
 * it is deliberately not a 302, because a GET that destroys a session is
 * CSRF-able from an <img> tag. The navigation must be a real top-level
 * assignment — Keycloak's end-session page cannot be fetched.
 */
export async function logout(csrfToken: string): Promise<string> {
  const { logoutUrl } = await bffRequest<{ logoutUrl: string }>('/bff/logout', {
    method: 'POST',
    csrfToken,
  })
  return logoutUrl
}

/** POST /bff/biller — internal users only. The selection becomes server-side
 *  session state; the browser never sends an X-Biller-Id header itself. That
 *  is the tenant-isolation boundary for the whole product. */
export const selectBiller = (billerId: string, csrfToken: string): Promise<void> =>
  bffRequest<void>('/bff/biller', { method: 'POST', body: { billerId }, csrfToken })

/**
 * The one place a `/bff/login` URL is built.
 *
 * Three things ride on it and each was added after somebody noticed it
 * missing: `returnTo` so a deep link survives sign-in (MPAY-32122),
 * `ui_locales` so Keycloak's own screens are in the language the user chose
 * (MPAY-32208), and `kc_action` for the identity-provider actions the Profile
 * screen starts. Built here rather than at each call site because "the sign-in
 * flow forgot one of these" is exactly the defect this function exists to stop
 * happening a third time.
 *
 * Every value is validated again by the BFF; this is convenience, not a
 * boundary.
 */
export function loginHref(options: {
  readonly returnTo?: string | null
  readonly uiLocales?: string | null
  readonly kcAction?: string | null
} = {}): string {
  const params = new URLSearchParams()
  if (options.kcAction) params.set('kc_action', options.kcAction)
  if (options.returnTo) params.set('returnTo', options.returnTo)
  if (options.uiLocales) params.set('ui_locales', options.uiLocales)
  const query = params.toString()
  return query ? `/bff/login?${query}` : '/bff/login'
}
